import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Breadcrumbs, Link } from '@mui/material';
import { Star as StarIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import { useRecipe } from '../../context/RecipeContext';
import dashBackground from '../../images/dash.png';
import dashDietImage from '../../images/dash-diet.jpg';
import nutritionalFocusImage from '../../images/nutritional-focus.webp';
import dashDietHypertensionImage from '../../images/dash-diet-to-stop-hypertension.jpg';

const PAGE_URL = 'https://createadish.com/dietary-guide/dash';
const PAGE_TITLE = 'DASH Diet Guide: Manage Blood Pressure - Create A Dish';
const PAGE_DESCRIPTION = 'Dietary Approaches to Stop Hypertension. Focuses on reducing sodium and increasing intake of fruits, vegetables, and low-fat dairy to manage blood pressure.';
const PAGE_IMAGE = 'https://createadish.com/images/dash-og.png';

const DASH: React.FC = () => {
  const navigate = useNavigate();
  const { setMode } = useRecipe();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta name="keywords" content="DASH diet, blood pressure, hypertension, heart health, low sodium, medical diet" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:image" content={PAGE_IMAGE} />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={PAGE_URL} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "DASH Diet Guide: Manage Blood Pressure",
            "description": PAGE_DESCRIPTION,
            "image": PAGE_IMAGE,
            "author": { "@type": "Organization", "name": "Create A Dish" },
            "publisher": { "@type": "Organization", "name": "Create A Dish", "logo": { "@type": "ImageObject", "url": "https://createadish.com/logo.png" } },
            "datePublished": "2025-12-24",
            "mainEntityOfPage": { "@type": "WebPage", "@id": PAGE_URL },
            "keywords": "HeartHealth, Medical, LowSodium"
          })}
        </script>
      </Helmet>
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', maxWidth: '1200px', mx: 'auto' }}>
        <Box sx={{ height: { xs: '40vh', md: '50vh' }, backgroundImage: `url(${dashBackground})`, backgroundSize: 'cover', backgroundPosition: 'center', filter: 'blur(1px)', position: 'relative' }}>
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0, 0, 0, 0.5)', px: 2, textAlign: 'center' }}>
            <Typography variant="h3" component="h1" sx={{ color: 'white', fontWeight: 'bold', textShadow: '2px 2px 4px rgba(0,0,0,0.8)', mb: 2 }}>DASH Diet</Typography>
            <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold', textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>Manage Blood Pressure</Typography>
          </Box>
        </Box>
        <Box sx={{ p: { xs: 2, md: 4 }, flexGrow: 1 }}>
          <Breadcrumbs 
            separator={<NavigateNextIcon fontSize="small" />} 
            sx={{ 
              mb: 4,
              '& .MuiBreadcrumbs-separator': {
                color: 'primary.main'
              }
            }}
            aria-label="breadcrumb"
          >
            <Link
              underline="hover"
              onClick={() => navigate('/resources')}
              sx={{ 
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                color: 'text.primary',
                '&:hover': {
                  color: 'primary.main'
                }
              }}
            >
              Resources
            </Link>
            <Link
              underline="hover"
              onClick={() => navigate('/dietary-guide')}
              sx={{ 
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                color: 'text.primary',
                '&:hover': {
                  color: 'primary.main'
                }
              }}
            >
              Dietary Guide
            </Link>
            <Typography 
              sx={{ 
                fontSize: '1rem',
                fontWeight: 600,
                color: 'primary.main'
              }}
            >
              DASH Diet
            </Typography>
          </Breadcrumbs>
          <Typography variant="h4" component="h2" sx={{ textAlign: 'center', mb: 4, fontWeight: 'bold', color: 'primary.main' }}>The DASH Diet</Typography>
          <Typography variant="body1" sx={{ mb: 4, textAlign: 'center', maxWidth: '800px', mx: 'auto', lineHeight: 1.6, fontSize: { xs: '1rem', md: '1.1rem' } }}>{PAGE_DESCRIPTION}</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 4, flexWrap: 'wrap' }}>
            {['#HeartHealth', '#Medical', '#LowSodium'].map(tag => <Typography key={tag} variant="body2" sx={{ color: 'primary.main', fontWeight: 'bold' }}>{tag}</Typography>)}
          </Box>
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
            Welcome to CreateADish.com's guide to the DASH Diet (Dietary Approaches to Stop Hypertension), a proven eating plan that focuses on reducing sodium while boosting intake of fruits, vegetables, whole grains, and low-fat dairy to manage blood pressure and promote overall health. Developed through rigorous scientific research, it's flexible and sustainable. Our AI recipe generator can help you create DASH-compliant meals—input details like "low-sodium vegetable stir-fry" for customized, heart-healthy options.
          </Typography>
          <Box component="img" src={dashDietImage} alt="DASH diet overview" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Historical Background
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              The DASH Diet originated in the early 1990s as part of research funded by the National Heart, Lung, and Blood Institute (NHLBI) to find dietary ways to lower blood pressure without medication. The first major study, published in 1997, showed significant reductions in hypertension. Today, it's endorsed by health organizations worldwide.
            </Typography>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Key Components and Foods
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              The DASH Diet is nutrient-dense, aiming for 2,000-2,600 calories daily with a sodium cap of 2,300 mg (or 1,500 mg for stricter control).
            </Typography>
            
            <Typography variant="h5" component="h4" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
              Core Foods to Emphasize
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}><strong>Fruits and Vegetables</strong>: 4-5 servings each daily; bananas, oranges, apples, spinach, broccoli.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Whole Grains</strong>: 6-8 servings; brown rice, oatmeal, whole-wheat bread.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Low-Fat Dairy</strong>: 2-3 servings; skim milk, low-fat yogurt.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Lean Proteins</strong>: 6 or fewer ounces; fish, poultry, beans, nuts.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Healthy Fats</strong>: Limited; use oils like olive or canola sparingly.</Typography>
            </Box>
            <Box component="img" src={nutritionalFocusImage} alt="DASH nutritional focus" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />

            <Typography variant="h5" component="h4" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
              Foods to Limit or Avoid
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}>High-sodium items: Processed foods, canned soups, salty snacks.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Sugary beverages, sweets, red and processed meats.</Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Health Benefits
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              Backed by decades of research, the DASH Diet effectively lowers blood pressure, improves lipid profiles, aids weight loss, and reduces risks of heart disease (by 15%), stroke (by 27%), and diabetes.
            </Typography>
            <Box component="img" src={dashDietHypertensionImage} alt="DASH diet and hypertension" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Tips for Adopting the DASH Diet
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}><strong>Reduce Sodium Gradually</strong>: Start by reading labels and using herbs instead of salt.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Incorporate More Produce</strong>: Aim for half your plate to be fruits and veggies.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Use AI for Recipes</strong>: Generate ideas on CreateADish.com, like "DASH-friendly low-sodium soup."</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Track Progress</strong>: Monitor blood pressure and combine with exercise.</Typography>
            </Box>
          </Box>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h5" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>Ready to Create Your Perfect Dish?</Typography>
            <Button variant="contained" size="large" startIcon={<StarIcon />} onClick={() => { setMode('generate'); navigate('/composer'); }} sx={{ minWidth: 200, fontSize: '1.1rem', py: 1.5, px: 4 }}>Get Started</Button>
          </Box>
          <Box sx={{ textAlign: 'center', py: 3, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">© 2025 Create A Dish. Made with ❤️ for food lovers everywhere.</Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default DASH;
